// src/pages/EmployeeDocuments.jsx

import React, { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  FolderOpen,
  FileText,
  Download,
  Upload,
  Eye,
  Calendar,
  Shield,
  Award,
  CreditCard,
  User,
  Building2,
  Search,
  Filter,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  uploadEmployeeDocs,
  fetchEmployeeDocuments,
  deleteDocument,
  getSignedURL,
} from "@/services/employeeService";
import { fetchEmployeeDocumentsByAdmin } from "@/services/adminService";
import { UploadCloud, File, X } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { uploadEmloyeeDocsByAdmin } from "@/services/adminService";
import { getErrorMessage, isAuthExpiryError } from "@/utils/errorHandler";
import { Skeleton } from "@/components/ui/skeleton";
// ✅ Capacitor (mobile download)
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";

const EmployeeDocuments = (employeeId) => {
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [selectedDocument, setSelectedDocument] = useState(null);
  const fileInputRef = useRef(null);
  const [viewSelectedDoc, setViewSelectedDoc] = useState();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ name: "", category: "" });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [initialLoading, setInitialLoading] = useState(true);
  const [uploadLoading, setUploadLoading] = useState(false);
  const isNative = Capacitor.isNativePlatform(); // ✅ used to hide View button on mobile

  const upload = async () => {
    if (!form.category || !form.name || !file) {
      toast.error("All fields are required");
      return;
    }
    setUploadLoading(true);
    const formData = new FormData();
    formData.append("fileName", form.name);
    formData.append("category", form.category.toLowerCase());
    formData.append("file", file);

    try {
      if (employeeId.employeeId) {
        await uploadEmloyeeDocsByAdmin(formData, employeeId);
      } else {
        await uploadEmployeeDocs(formData);
      }
      fetchDocuments();
      setOpen(false);
      toast.success(`${form.category} document uploaded successfully!`);
      setForm({ name: "", category: "" });
      setFile(null);
    } catch (e) {
      toast.error(e.message || `Failed to upload document`);
    } finally {
      setUploadLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  useEffect(() => {
    filterDocuments();
    setCurrentPage(1);
  }, [documents, searchTerm, filterCategory]);


  const fetchDocuments = async () => {
    let data = [];
    try {
      if (employeeId.employeeId) {
        data = await fetchEmployeeDocumentsByAdmin(employeeId);
      } else {
        data = await fetchEmployeeDocuments();
      }

      const normalizeCategory = (c) =>
        (c || "other").toString().trim().toLowerCase();

      const normalizeType = (t) => {
        const v = (t || "").toString().trim().toLowerCase();
        if (v === "application/pdf" || v.endsWith("/pdf") || v === "pdf")
          return "pdf";
        if (
          v === "image/jpeg" ||
          v === "image/jpg" ||
          v === "jpg" ||
          v === "jpeg"
        )
          return "jpg";
        if (v === "image/png" || v === "png") return "png";
        return v || "unknown";
      };

      const mockDocuments = data.map((d) => ({
        id: d._id,
        name: d.fileName,
        category: normalizeCategory(d.category),
        type: normalizeType(d.file_type),
        size: d.file_size,
        uploaded_date: d.createdAt,
        url: d.file_url,
      }));

      setDocuments(mockDocuments);
    } catch (e) {
      toast.error(e.message || "Failed to load documents.");
    } finally {
      setInitialLoading(false);
    }
  };

  const filterDocuments = () => {
    let filtered = documents;

    if (searchTerm) {
      filtered = filtered.filter((doc) =>
        doc.name.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    if (filterCategory !== "all") {
      filtered = filtered.filter((doc) => doc.category === filterCategory);
    }

    setFilteredDocuments(filtered);
  };

  const totalPages = Math.ceil(filteredDocuments.length / pageSize);

  const paginatedDocuments = filteredDocuments.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  // ---------------------------------------------
  // ✅ Helpers for mobile download
  // ---------------------------------------------
  const getExtensionFromTypeOrName = (doc) => {
    const name = (doc?.name || "").toString();
    const lower = name.toLowerCase();

    if (lower.endsWith(".pdf") || doc.type === "pdf") return "pdf";
    if (lower.endsWith(".png") || doc.type === "png") return "png";
    if (
      lower.endsWith(".jpg") ||
      lower.endsWith(".jpeg") ||
      doc.type === "jpg" ||
      doc.type === "jpeg"
    )
      return "jpg";

    // fallback
    return "bin";
  };

  const safeFilename = (doc) => {
    const base = (doc?.name || "document").toString().trim();
    const ext = getExtensionFromTypeOrName(doc);

    // If the name already includes an extension, keep it.
    const hasExt = /\.[a-z0-9]{1,6}$/i.test(base);
    const raw = hasExt ? base : `${base}.${ext}`;

    // sanitize for filesystem
    return raw.replace(/[<>:"/\\|?*\x00-\x1F]/g, "_");
  };

  const blobToBase64 = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onloadend = () => {
        const dataUrl = reader.result;
        const base64 = String(dataUrl).split(",")[1] || "";
        resolve(base64);
      };
      reader.readAsDataURL(blob);
    });

  const handleDownload = async (doc) => {
    try {
      const signedUrl = await getSignedURL(doc.url);
      if (!signedUrl) {
        toast.error("Failed to get download link");
        return;
      }

      // ✅ Native (Android/iOS)
      if (isNative) {
        const response = await fetch(signedUrl);
        if (!response.ok) throw new Error("Failed to download file");

        const blob = await response.blob();
        const base64 = await blobToBase64(blob);

        const filename = safeFilename(doc);

        // Save in app documents folder
        const writeResult = await Filesystem.writeFile({
          path: filename,
          data: base64,
          directory: Directory.Documents,
          recursive: true,
        });

        toast.success(`Saved ${filename}`);

        // Share sheet so user can "Save to Files"/Drive/WhatsApp etc.
        await Share.share({
          title: "Download document",
          text: filename,
          url: writeResult.uri, // important: share the file URI
          dialogTitle: "Open / Save document",
        });

        return;
      }

      // ✅ Web/Desktop
      const response = await fetch(signedUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = safeFilename(doc);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Downloading ${doc.name}`);
    } catch (err) {
      console.error(err);
      toast.error("Download failed");
    }
  };

  const openDeleteConfirm = (doc) => {
    setDocToDelete(doc);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!docToDelete) return;

    try {
      const res = await deleteDocument(docToDelete.id, employeeId);
      if (res) {
        toast.success(`Deleted ${docToDelete.name}`);
        fetchDocuments();
      }

      setConfirmOpen(false);
      setDocToDelete(null);
    } catch (error) {
      if (!isAuthExpiryError(error)) {
        toast.error(getErrorMessage(error, "Failed to delete document"));
      }
    }
  };

  const handlePreview = async (document) => {
    try {
      // ❌ Hidden on mobile (native)
      if (isNative) return;

      setViewSelectedDoc(null);
      if (!document) return;
      const res = await getSignedURL(document.url);

      if (document.type === "pdf") {
        window.open(res, "_blank");
        return;
      }
      setViewSelectedDoc(res);
      setSelectedDocument(document);
    } catch (error) {
      if (!isAuthExpiryError(error)) {
        toast.error(getErrorMessage(error, "Failed to preview document"));
      }
    }
  };

  const getDocumentIcon = (category) => {
    const icons = {
      Employment: Shield,
      Identity: User,
      Banking: CreditCard,
      Education: Award,
      Experience: Building2,
      Medical: FileText,
      Tax: Calendar,
      other: FileText,
    };
    const key = (category || "other").toString().trim().toLowerCase();
    const Icon = icons[key] || FileText;
    return <Icon className="h-5 w-5 text-primary" />;
  };

  const getCategoryStats = () => {
    const stats = {};
    documents.forEach((doc) => {
      stats[doc.category] = (stats[doc.category] || 0) + 1;
    });
    return stats;
  };

  const categoryStats = getCategoryStats();
  const categories = [
    { label: "Employment", value: "employment" },
    { label: "Identity", value: "identity" },
    { label: "Banking", value: "banking" },
    { label: "Education", value: "education" },
    { label: "Experience", value: "experience" },
    { label: "Medical", value: "medical" },
    { label: "Tax", value: "tax" },
    { label: "Other", value: "other" },
  ];
  const getCategoryLabel = (value) => {
    const found = categories.find((c) => c.value === value);
    return found ? found.label : value;
  };

  if (initialLoading) {
    return <EmployeeDocumentsSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          {!employeeId.employeeId && (
            <>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                My Documents
              </h1>
              <p className="text-gray-500">
                View and manage your employment documents
              </p>
            </>
          )}
        </div>
        <Button
          className="flex items-center space-x-2"
          onClick={() => setOpen(true)}
        >
          <Upload className="h-4 w-4" />
          <span>Upload Document</span>
        </Button>
      </div>

      {/* Document Categories Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-3">
        {categories.map((category) => (
          <Card
            key={category.value}
            className="cursor-pointer hover:shadow-sm transition-all p-0"
          >
            <CardContent className="p-3 text-center">
              <div className="w-8 h-8 bg-primary/10 rounded-md flex items-center justify-center mx-auto mb-1">
                {getDocumentIcon(category.label)}
              </div>

              <p className="text-xs font-medium text-gray-700 truncate">
                {category.label}
              </p>

              <p className="text-sm font-bold text-primary">
                {categoryStats[category.value] || 0}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter Category" />
                </SelectTrigger>

                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>

                  {categories.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Documents Table */}
      <Card>
        <CardHeader>
          <CardTitle>Document List</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {filteredDocuments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center border rounded-xl bg-muted/30">
              <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-5">
                <FileText className="h-7 w-7 text-muted-foreground" />
              </div>

              <h3 className="text-lg font-semibold">
                {searchTerm || filterCategory !== "all"
                  ? "No matching documents"
                  : "No documents uploaded yet"}
              </h3>

              <p className="text-sm text-muted-foreground mt-2 max-w-sm">
                {searchTerm || filterCategory !== "all"
                  ? "Try adjusting your search or filters to find what you're looking for."
                  : "Documents will appear here once uploaded."}
              </p>
            </div>
          ) : (
            <>
              <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-x-auto thin-scrollbar" style={{scrollbarWidth: "thin"}}>
                <div className="min-w-[800px] thin-scrollbar" style={{ scrollbarWidth: 'thin' }}>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/40 border-b dark:border-muted-foreground/40">
                      <TableHead>Document</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead>Uploaded</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>

                  <TableBody>
                    {paginatedDocuments.map((document) => (
                      <TableRow
                        key={document.id}
                        onClick={() => {
                          if (!isNative) handlePreview(document);
                        }}
                        className="hover:bg-muted/40 transition-colors border-b dark:border-muted-foreground/40 cursor-pointer"
                      >
                        {/* Document */}
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
                              {getDocumentIcon(document.category)}
                            </div>
                            <div>
                              <p className="font-medium">{document.name}</p>
                            </div>
                          </div>
                        </TableCell>

                        {/* Category */}
                        <TableCell>
                          {getCategoryLabel(document.category)}
                        </TableCell>

                        {/* Type */}
                        <TableCell>
                          <Badge variant="secondary" className="uppercase">
                            {document.type}
                          </Badge>
                        </TableCell>

                        {/* Size */}
                        <TableCell>{document.size}</TableCell>

                        {/* Uploaded */}
                        <TableCell>
                          {format(
                            new Date(document.uploaded_date),
                            "dd MMM yyyy",
                          )}
                        </TableCell>

                        {/* Actions */}
                        <TableCell
                          className="text-right"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex justify-end gap-2">
                            {!isNative && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handlePreview(document)}
                                className="hover:bg-muted hover:text-foreground"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            )}

                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDownload(document)}
                              className="hover:bg-muted hover:text-foreground"
                            >
                              <Download className="w-4 h-4" />
                            </Button>

                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openDeleteConfirm(document)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              </div>

              {filteredDocuments.length > 0 && (
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  {/* Rows Per Page */}
                  <div className="flex items-center justify-between md:justify-start gap-3 text-sm w-full md:w-auto">
                    <span className="text-muted-foreground">Rows per page</span>
                    <Select
                      value={String(pageSize)}
                      onValueChange={(value) => setPageSize(Number(value))}
                    >
                      <SelectTrigger className="w-20 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[5, 10, 20, 50].map((size) => (
                          <SelectItem key={size} value={String(size)}>
                            {size}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Pagination Controls */}
                  <div className="flex items-center justify-between md:justify-end gap-3 w-full md:w-auto">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((prev) => prev - 1)}
                    >
                      Prev
                    </Button>

                    <span className="text-sm text-muted-foreground whitespace-nowrap">
                      Page {currentPage} of {totalPages || 1}
                    </span>

                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage === totalPages || totalPages === 0}
                      onClick={() => setCurrentPage((prev) => prev + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={open}
        onOpenChange={(isOpen) => {
          setOpen(isOpen);
          if (!isOpen) {
            setForm({ name: "", category: "" });
            setFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <Select
              value={form.category}
              onValueChange={(value) => setForm({ ...form, category: value })}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select Category" />
              </SelectTrigger>

              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Input
              placeholder="Document Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />

            <div
              className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all hover:bg-gray-50 dark:hover:bg-gray-900/30"
              onClick={() => fileInputRef.current?.click()}
            >
              {!file ? (
                <div className="text-gray-500 flex flex-col items-center">
                  <UploadCloud className="w-8 h-8 mb-2 text-gray-400" />
                  <p className="font-medium">Click to upload file</p>
                  <p className="text-xs text-gray-500 mt-1">
                    PDF, JPG, PNG • Max size 10MB
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-[1fr_auto] items-center gap-2 bg-primary/10 p-2 rounded w-full overflow-hidden">
                  <div className="flex items-center min-w-0">
                    <File className="w-4 h-4 mr-2 flex-shrink-0" />
                    <span
                      className="truncate overflow-hidden whitespace-nowrap"
                      title={file.name}
                    >
                      {file.name}
                    </span>
                  </div>

                  <X
                    className="w-5 h-5 cursor-pointer text-red-500 hover:text-red-600 flex-shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                    }}
                  />
                </div>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              onChange={(e) => {
                const selected = e.target.files[0];
                if (!selected) return;

                const allowedTypes = [
                  "application/pdf",
                  "image/jpeg",
                  "image/png",
                ];
                const maxSizeMB = 10;

                if (selected.size / (1024 * 1024) > maxSizeMB) {
                  toast.error("File size must be less than 10MB");
                  e.target.value = "";
                  return;
                }

                if (!allowedTypes.includes(selected.type)) {
                  toast.error("Only PDF, JPG, and PNG files are allowed");
                  e.target.value = "";
                  return;
                }

                setFile(selected);
              }}
              hidden
            />

            <div className="bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 p-3 rounded-lg">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Upload Guidelines
              </p>

              <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1 ml-4 list-disc">
                <li>
                  Maximum file size: <strong>10MB</strong>
                </li>
                <li>
                  Supported formats: <strong>PDF, JPG, PNG</strong>
                </li>
                <li>Ensure the document is clear and readable</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={upload}
              disabled={uploadLoading || !form.category || !form.name || !file}
            >
              {uploadLoading ? "Uploading..." : "Upload"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!selectedDocument}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setSelectedDocument(null);
            setViewSelectedDoc(null);
          }
        }}
      >
        <DialogContent className="max-w-[95vw] sm:max-w-4xl dark:border-foreground/40">
          <DialogHeader>
            <DialogTitle className="truncate pr-6 text-base sm:text-lg">
              {selectedDocument?.name || "Preview"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center bg-muted/40 dark:bg-muted/20 rounded-md overflow-auto max-h-[70vh]">
            {viewSelectedDoc && (
              <img
                src={viewSelectedDoc}
                alt={selectedDocument?.name || "Document preview"}
                className="max-w-full max-h-[70vh] object-contain"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={confirmDelete}
        title="Delete Document?"
        message={`Are you sure you want to delete "${docToDelete?.name}"? This action cannot be undone.`}
      />
    </div>
  );
};

export default EmployeeDocuments;

function EmployeeDocumentsSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-44" />
          <Skeleton className="h-4 w-72" />
        </div>
        <Skeleton className="h-10 w-40" />
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8">
        {[...Array(8)].map((_, index) => (
          <Card key={index}>
            <CardContent className="p-3 text-center space-y-2">
              <Skeleton className="mx-auto h-8 w-8 rounded-md" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="mx-auto h-4 w-8" />
            </CardContent>
          </Card>
        ))}
      </div>
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex flex-col gap-4 sm:flex-row">
            <Skeleton className="h-10 flex-1" />
            <Skeleton className="h-10 w-[180px]" />
          </div>
          {[...Array(4)].map((_, index) => (
            <Skeleton key={index} className="h-14 w-full" />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title = "Are you sure?",
  message = "This action cannot be undone.",
}) => {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          {message}
        </p>

        <DialogFooter className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onClose(false)}>
            Cancel
          </Button>

          <Button variant="destructive" onClick={onConfirm}>
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
